'use client'
import type { PropsWithChildren } from 'react'
import classNames from 'classnames'
import { MDSForm, type MDSFormProps } from './mds-form'
import { GreenhouseIframeForm } from './greenhouse-iframe-form'
import { HDSPlusStandaloneLink } from '@hashicorp/react-mds/src/hds-plus/standalone-link'
import FlightIcon from '@hashicorp/react-mds/src/hds/components/flight-icon'
import s from './style.module.css'
import {
	useApplicationForm,
	ApplicationFormProvider,
	type ApplicationFormProviderProps,
} from 'lib/careers/context'
import { useRouter } from 'next/navigation'
import { postFormDataToProxy } from 'lib/careers/greenhouse-careers'
import {
	assessUserInvisible,
	assessUserCheckbox,
	BOT_THRESHOLD,
	InvisibleLoader,
	CheckboxLoader,
} from 'lib/recaptcha'
import { logger } from 'lib/datadog/rum-logger'

const CHECKBOX_DOM_ID = 'recaptcha-checkbox'

/**
 *
 * There are 2 application forms that more or less do the same thing.
 *
 * This component adds checks to decide which version of the form to render
 *
 * Why do we have 2 forms?
 *
 * {@link MDSForm} form matches the Hashicorp Design System and allows us granulairty to gather more insights into how interact with our components
 * We have no insight into what goes on in the greenhouse iframe form, rendered by {@link GreenhouseIframeForm}
 *
 * However, we aren't currently at parity with the Greenhouse iframe forms so we still need to fallback to the iframe for some features
 *
 * Specifically,
 * - we don't have a locations feature with the google maps api
 * - we don't have greenhouse's index of schools, major, and degree types for education questions (only asked to interns)
 *
 * This means that we fall back to the greenhouse-iframe for onsite / hybrid jobs
 * and internships
 */
function Form({ career, children }: PropsWithChildren<MDSFormProps>) {
	const { hideApplication, formState } = useApplicationForm()
	const isFormVisible = formState.isVisible
	const hasAttemptedValidSubmission = formState.submissionStatus === 'pending'

	if (!isFormVisible) {
		return children
	}

	return (
		<div className={s.application}>
			<HDSPlusStandaloneLink
				text="Back to job posting"
				color="secondary"
				onClick={hideApplication}
				icon={<FlightIcon name="arrow-left" />}
			/>
			<h1 className={s.heading}>HashiCorp Employment Application</h1>
			<MDSForm career={career} />
			<div
				className={classNames(
					s.checkboxContainer,
					s[formState.submissionStatus]
				)}
				id={CHECKBOX_DOM_ID}
			></div>
			{hasAttemptedValidSubmission && <LinkedInTrackingPixels />}
			<CheckboxLoader />
			<InvisibleLoader />
		</div>
	)
}

export function LinkedInTrackingPixels() {
	// Add two LinkedIn tracking pixels to the page when an application submission is complete,
	// so we can appropriately track the success of our Campaigns.
	return (
		<>
			{/* eslint-disable-next-line @next/next/no-img-element */}
			<img
				height="1"
				width="1"
				style={{ display: 'none' }}
				alt=""
				src="https://px.ads.linkedin.com/collect/?pid=537212&conversionId=550876&fmt=gif"
			/>
		</>
	)
}

/* 
  2-TIERED RECAPTCHA ASSESSMENT
  -----------------------------
  1. User Clicks Submit (form fields already validated)
  2. Assess with invisible reCAPTCHA (v3), which outputs a token.
  3. Send token to POST /api/assess-recaptcha-token → returns a score.
     - If score > BOT_THRESHOLD → Proceed with submission. ✅ END
     - If score <= BOT_THRESHOLD → Require checkbox reCAPTCHA (v2).
  
  4. Checkbox reCAPTCHA outputs a new token.
  5. Send token to POST /api/assess-recaptcha-token → returns a score.
     - If score > BOT_THRESHOLD → Proceed with submission. ✅ END
     - If score <= BOT_THRESHOLD → return and go to form success callback. 🚨 END
       
  🔹 If a bot is caught at the checkbox stage, they must solve a puzzle to proceed. 
     - Only when solved does reCAPTCHA output a score.
     - Ideally, bots get stuck here, preventing fake submissions. however at this stage regardless of the score, once the puzzle is solved, we allow the submission to go through.

*/
const guardedSubmission = async (formData: FormData, listingId: string) => {
	logger.info('careers_application_submit_attempt', { listingId })
	const action = 'careers_submit_application'

	// form data is not serializable, we need to convert it to a plain object for logging
	const formDataLog = Object.fromEntries(formData.entries())

	const invisibleTokenAsessmentRes = await assessUserInvisible({
		action,
		ctx: { listingId, formData: formDataLog },
	})

	// recaptcha check failed, treat as submission error (the submit handler will catch and handle this)
	const failedToAssess = 'error' in invisibleTokenAsessmentRes
	if (failedToAssess) {
		logger.error('careers_application_submit_error', {
			listingId,
			error: invisibleTokenAsessmentRes.error,
			formattedFormData: formDataLog,
		})
		throw new Error('Failed to assess user', {
			cause: invisibleTokenAsessmentRes.error,
		})
	}

	const isSuspectedBotTierOne =
		'score' in invisibleTokenAsessmentRes &&
		invisibleTokenAsessmentRes.score <= BOT_THRESHOLD

	if (isSuspectedBotTierOne) {
		logger.info('careers_application_submit_bot', {
			listingId,
			formData: formDataLog,
			...invisibleTokenAsessmentRes,
		})

		const checkboxTokenAssessmentRes = await assessUserCheckbox({
			action: 'careers_submit_application',
			ctx: { listingId, formData: formDataLog },
			containerId: CHECKBOX_DOM_ID,
		})

		// recaptcha check failed, treat as submission error (the submit handler will catch and handle this)
		const failedToAssess = 'error' in checkboxTokenAssessmentRes
		if (failedToAssess) {
			logger.error('careers_application_submit_bot', {
				listingId,
				error: checkboxTokenAssessmentRes.error,
				formattedFormData: formDataLog,
			})
			throw new Error('Failed to assess user', {
				cause: checkboxTokenAssessmentRes.error,
			})
		}
		const isSuspectedBotTierTwo =
			'score' in checkboxTokenAssessmentRes &&
			checkboxTokenAssessmentRes.score <= BOT_THRESHOLD

		if (isSuspectedBotTierTwo) {
			//puzzle was solved so despite low score, put it through
			logger.warn('careers_application_submit_bot_puzzle_solved', {
				listingId,
				score: checkboxTokenAssessmentRes.score,
				formattedFormData: formDataLog,
			})
		}
	}

	await postFormDataToProxy(formData, listingId)
}

function ApplicationFormWrapper({
	career,
	children,
	initialFormState,
	...rest
}: ApplicationFormProviderProps) {
	const router = useRouter()

	return (
		<ApplicationFormProvider
			career={career}
			initialFormState={initialFormState}
			onSubmitClick={guardedSubmission}
			onSubmitSuccess={() => {
				router.push('/thank-you/job-listing')
			}}
			onSubmitError={(err) => {
				logger.error('careers_application_submit_error', err)
				window.scrollTo(0, 0)
			}}
			{...rest}
		>
			<Form career={career}>{children}</Form>
		</ApplicationFormProvider>
	)
}

export { ApplicationFormWrapper as ApplicationForm }
